import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Truck, Clock, Calendar, FileText, Upload } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchTransferRecieptOthersQueue,
    fetchTransferRecieptOtherItems,
    verifyTransferRecieptOther,
    verifyItemTransferVendorOthers,
    clearTransferRecieptOtherItems,
    clearVerifyResult,
    resetTransferRecieptOthersVerification,
    selectTROQueue, selectTROQueueLoading, selectTROQueueError,
    selectTROItems, selectTROItemsLoading,
    selectTROVerifyLoading,
} from '../../slices/purchaseSlice/transferRecieptOthersVerificationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import { buildMrrUrl, S3_FOLDERS } from '../../config/s3Config';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';

const num = (v) => parseFloat(v || 0) || 0;

// Legacy's own dropdown-building JS excludes the "Return" status in both branches (the "From
// Vendor" branch's own exclusion condition covers every real CC type, making it effectively
// always-excluded there too) — replicated as a flat exclude rather than the dead conditional.
const EXCLUDED_ACTIONS = ['return'];

const ConfirmModal = ({ title, message, confirmLabel = 'OK', cancelLabel = 'Cancel', onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
            </div>
            <div className="p-5 text-sm text-gray-700 dark:text-gray-300">{message}</div>
            <div className="p-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={onCancel} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold">{cancelLabel}</button>
                <button onClick={onConfirm} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold">{confirmLabel}</button>
            </div>
        </div>
    </div>
);

const TransferRecieptOthersVerification = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    const queue = useSelector(selectTROQueue);
    const queueLoading = useSelector(selectTROQueueLoading);
    const queueError = useSelector(selectTROQueueError);
    const items = useSelector(selectTROItems);
    const itemsLoading = useSelector(selectTROItemsLoading);
    const verifyLoading = useSelector(selectTROVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [checkedRows, setCheckedRows] = useState({});
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // holds the action awaiting a confirm modal
    const [reuploading, setReuploading] = useState(false);
    const [reuploadProgress, setReuploadProgress] = useState(0);

    const isVendor = selectedItem?.Type === 'From Vendor';

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    useEffect(() => {
        if (roleId && uid) dispatch(fetchTransferRecieptOthersQueue({ roleId, uid }));
    }, [roleId, uid, dispatch]);

    useEffect(() => {
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetTransferRecieptOthersVerification());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem) {
            dispatch(fetchTransferRecieptOtherItems({ mrrno: selectedItem.Mrrno, type: selectedItem.Type }));
            dispatch(fetchStatusList({ MOID: selectedItem.Moid, ROID: roleId, ChkAmt: 0 }));
            dispatch(setSelectedMOID(selectedItem.Moid));
            dispatch(fetchRemarks({ trno: selectedItem.Mrrno, moid: selectedItem.Moid }));
            setCheckedRows({});
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId && uid) dispatch(fetchTransferRecieptOthersQueue({ roleId, uid }));
        if (selectedItem) dispatch(fetchTransferRecieptOtherItems({ mrrno: selectedItem.Mrrno, type: selectedItem.Type }));
    };

    const handleItemSelect = (item) => setSelectedItem(item);

    // Re-uploads to the EXACT S3 key (S3_FOLDERS.MRR + the filename already stored in
    // MRReciept.DownloadPath) the existing "View QMQ Certificate" link already points at —
    // fixes a broken/missing attachment without touching the DB, since the link is derived
    // from the same filename.
    const handleReuploadCertificate = async (file, fileName) => {
        if (!file || !fileName) return;
        setReuploading(true);
        setReuploadProgress(0);
        try {
            await uploadFileToS3(file, S3_FOLDERS.MRR, fileName, setReuploadProgress);
            toast.success('Certificate uploaded — the "View QMQ Certificate" link should work now.');
        } catch (err) {
            const detail = err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || 'Unknown error';
            toast.error(`Upload failed: ${detail}`);
        } finally {
            setReuploading(false);
            setReuploadProgress(0);
        }
    };

    const toggleRow = (idx) => setCheckedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
    const toggleAllRows = (checked) => {
        const next = {};
        items.forEach((_, idx) => { next[idx] = checked; });
        setCheckedRows(next);
    };
    const allRowsChecked = items.length > 0 && items.every((_, idx) => checkedRows[idx]);

    const totals = useMemo(() => {
        if (isVendor) {
            return items.reduce((acc, r) => {
                acc.RaisedQty += num(r.RaisedQty);
                acc.RecievedQty += num(r.RecievedQty);
                acc.PreviousRecievedQty += num(r.PreviousRecievedQty);
                acc.PendingVerificationQty += num(r.PendingVerificationQty);
                return acc;
            }, { RaisedQty: 0, RecievedQty: 0, PreviousRecievedQty: 0, PendingVerificationQty: 0 });
        }
        return items.reduce((acc, r) => {
            acc.IssuedQty += num(r.IssuedQty);
            acc.LostQty += num(r.LostQty);
            acc.DamagedQty += num(r.DamagedQty);
            return acc;
        }, { IssuedQty: 0, LostQty: 0, DamagedQty: 0 });
    }, [items, isVendor]);

    const buildOtherStorePayload = (actionValue) => ({
        Mrrno: selectedItem.Mrrno,
        Remarks: comment.trim(),
        Appstatus: actionValue,
        SumLostQty: totals.LostQty,
        SumDamagedQty: totals.DamagedQty,
        Roleid: roleId,
        CreatedBy: getCurrentUser(),
    });

    // MrrStatus, Type(->@MrrType) and the per-item CSV fields here are bound exactly as this
    // backend's PurchaseDataLayer.verifyitemtransferVendorothers reads them off TransferReciept —
    // not the (differently-named) legacy MVC JSON keys.
    const buildVendorPayload = (actionValue, mrrStatus) => {
        const currentMrrStatus = (items[0]?.CurrentMrrStatus || '').trim();
        return {
            Mrrno: selectedItem.Mrrno,
            Remarks: comment.trim(),
            Appstatus: actionValue,
            SumRaisedQty: totals.RaisedQty,
            SumRecievedQty: totals.RecievedQty + totals.PreviousRecievedQty,
            ItemCodes: items.map((r) => r.ItemCode).join(',') + ',',
            RecievedQty: items.map((r) => num(r.RecievedQty)).join(',') + ',',
            PreviousRecievedQty: items.map((r) => num(r.PreviousRecievedQty)).join(',') + ',',
            RaisedQty: items.map((r) => num(r.RaisedQty)).join(',') + ',',
            Basic: items.map((r) => num(r.Basic)).join(',') + ',',
            Type: selectedItem.MrrType,
            MrrStatus: mrrStatus,
            CurrentMrrStatus: currentMrrStatus,
            Roleid: roleId,
            CreatedBy: getCurrentUser(),
        };
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchTransferRecieptOthersQueue({ roleId, uid }));
            setSelectedItem(null);
            setCheckedRows({});
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearTransferRecieptOtherItems());
            dispatch(clearVerifyResult());
            dispatch(resetApprovalData());
        }, 800);
    };

    const submitOtherStore = async (actionValue) => {
        try {
            const result = await dispatch(verifyTransferRecieptOther(buildOtherStorePayload(actionValue))).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status && status !== 'Submitted') { toast.error(status); return; }
            toast.success(`MRR ${selectedItem.Mrrno} ${actionValue}d successfully`);
            finishAndRefresh();
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit verification');
        }
    };

    const submitVendor = async (actionValue, mrrStatus) => {
        try {
            const result = await dispatch(verifyItemTransferVendorOthers(buildVendorPayload(actionValue, mrrStatus))).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status && status !== 'Submitted') { toast.error(status); return; }
            toast.success(`MRR ${selectedItem.Mrrno} ${actionValue}d successfully`);
            finishAndRefresh();
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit verification');
        }
    };

    const handleActionClick = (action) => {
        if (!selectedItem) { toast.error('No MRR selected'); return; }
        if (!comment.trim()) { toast.error('Enter Notes'); return; }
        if (items.length === 0) { toast.error('Invalid Submission'); return; }
        if (!allRowsChecked) { toast.error('Please Verify Item Codes'); return; }

        const actionValue = action.value || action.type;

        if (!isVendor) {
            const totalLostDamaged = totals.LostQty + totals.DamagedQty;
            if (actionValue === 'Approve' && totalLostDamaged > 0) {
                setPendingAction({ kind: 'otherStoreConfirm', actionValue });
                return;
            }
            submitOtherStore(actionValue);
            return;
        }

        // Vendor branch
        const currentMrrStatus = (items[0]?.CurrentMrrStatus || '').trim();
        const needsCloseConfirm = actionValue === 'Approve' && selectedItem.MrrType === 'Multi'
            && totals.RaisedQty !== (totals.RecievedQty + totals.PreviousRecievedQty)
            && (currentMrrStatus === 'Open' || currentMrrStatus === '');

        if (needsCloseConfirm) {
            setPendingAction({ kind: 'vendorCloseConfirm', actionValue });
            return;
        }
        submitVendor(actionValue, 'Close');
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.Mrrno?.toLowerCase().includes(q) || item.VendorName?.toLowerCase().includes(q) || item.PoNo?.toLowerCase().includes(q) || item.CCCode?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">MRR {item.Mrrno}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.VendorName || item.CCCode}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1"><Calendar className="w-3 h-3" /><span>{item.Date}</span></span>
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">{item.MrrType}</span>
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[120px] truncate">MRR {item.Mrrno}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.CCCode}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]"><Calendar className="w-3 h-3" /> {item.Date}</span>
            <span className="ml-auto px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">{item.MrrType}</span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Truck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetailContent = () => {
        if (!selectedItem) return null;
        const fileItem = items.find((r) => r.FileName);

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">MRR {selectedItem.Mrrno}</h2>
                            <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{selectedItem.VendorName}</p>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{selectedItem.Type}</span>
                                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">{selectedItem.MrrType} MRR</span>
                                {selectedItem.CCCode && <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">CC: {selectedItem.CCCode}</span>}
                                {selectedItem.PoNo && <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">PO: {selectedItem.PoNo}</span>}
                            </div>
                        </div>
                        {fileItem && (
                            <div className="flex flex-col items-end gap-1.5">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => window.open(buildMrrUrl(fileItem.FileName), '_blank', 'noopener,noreferrer')}
                                        className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all text-xs font-medium"
                                    >
                                        View QMQ Certificate
                                    </button>
                                    <label className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-1.5">
                                        <Upload className="w-3.5 h-3.5" />
                                        {reuploading ? `Uploading… ${reuploadProgress}%` : 'Replace'}
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            className="hidden"
                                            disabled={reuploading}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                e.target.value = '';
                                                if (file) handleReuploadCertificate(file, fileItem.FileName);
                                            }}
                                        />
                                    </label>
                                </div>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">If the link above 404s, use Replace to re-upload to the same file name.</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg"><FileText className="w-4 h-4 text-white" /></div>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Item Details</h3>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input type="checkbox" checked={allRowsChecked} onChange={(e) => toggleAllRows(e.target.checked)} />
                            Select All
                        </label>
                    </div>

                    {itemsLoading ? (
                        <div className="flex items-center justify-center gap-3 py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div><span className="text-sm text-gray-500">Loading items…</span></div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No item details found.</p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                <thead className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20">
                                    <tr>
                                        <th className="px-2 py-1.5 text-center font-semibold text-gray-700 dark:text-gray-300">Chk</th>
                                        {!isVendor && <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Sending CC</th>}
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Code</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Name</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Specification</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Units</th>
                                        {!isVendor ? (
                                            <>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Amount</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Issued Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Lost Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Damaged Qty</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Basic</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Raised Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Received Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Prev Approved Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Pending Verif. Qty</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {items.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                            <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!checkedRows[idx]} onChange={() => toggleRow(idx)} /></td>
                                            {!isVendor && <td className="px-2 py-1 whitespace-nowrap">{row.CCCode}</td>}
                                            <td className="px-2 py-1 whitespace-nowrap font-medium">{row.ItemCode}</td>
                                            <td className="px-2 py-1">{row.ItemName}</td>
                                            <td className="px-2 py-1">{row.Specification}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{row.Units}</td>
                                            {!isVendor ? (
                                                <>
                                                    <td className="px-2 py-1 text-right">{num(row.Amount).toFixed(2)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.IssuedQty).toFixed(4)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.LostQty).toFixed(4)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.DamagedQty).toFixed(4)}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-2 py-1 text-right">{num(row.Basic).toFixed(2)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.RaisedQty).toFixed(4)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.RecievedQty).toFixed(4)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.PreviousRecievedQty).toFixed(4)}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.PendingVerificationQty).toFixed(4)}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-2 py-1" colSpan={isVendor ? 5 : 6}>TOTAL</td>
                                        {!isVendor ? (
                                            <>
                                                <td></td>
                                                <td className="px-2 py-1 text-right">{totals.IssuedQty.toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{totals.LostQty.toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{totals.DamagedQty.toFixed(4)}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td></td>
                                                <td className="px-2 py-1 text-right">{totals.RaisedQty.toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{totals.RecievedQty.toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{totals.PreviousRecievedQty.toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{totals.PendingVerificationQty.toFixed(4)}</td>
                                            </>
                                        )}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                    </div>
                ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this MRR</p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={verifyLoading}
                        isVerified={allRowsChecked}
                        comment={comment}
                        showValidation={true}
                        excludeActions={EXCLUDED_ACTIONS}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'MRR Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Truck}
                badgeText="MRR Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by MRR no, vendor, PO, CC…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No MRRs pending verification!',
                        itemKey: 'Mrrno',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'MRR Verification',
                        icon: Truck,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'MRR Details',
                        icon: Truck,
                        selectedTitle: 'MRR Verification',
                        emptyTitle: 'No MRR Selected',
                        emptyMessage: 'Select an MRR from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />

            {pendingAction?.kind === 'otherStoreConfirm' && (
                <ConfirmModal
                    title="Confirm Approval"
                    message={`This MRR has Lost/Damaged quantity (Total: ${(totals.LostQty + totals.DamagedQty).toFixed(4)}). Do you want to proceed with Approve?`}
                    confirmLabel="OK"
                    cancelLabel="Cancel"
                    onCancel={() => setPendingAction(null)}
                    onConfirm={() => { const a = pendingAction.actionValue; setPendingAction(null); submitOtherStore(a); }}
                />
            )}

            {pendingAction?.kind === 'vendorCloseConfirm' && (
                <ConfirmModal
                    title="Do You Want To Close the MRR"
                    message={`Received quantity does not match raised quantity for Multi MRR ${selectedItem?.Mrrno}. Do you really want to close it?`}
                    confirmLabel="Yes"
                    cancelLabel="No"
                    onCancel={() => { const a = pendingAction.actionValue; setPendingAction(null); submitVendor(a, 'Open'); }}
                    onConfirm={() => { const a = pendingAction.actionValue; setPendingAction(null); submitVendor(a, 'Close'); }}
                />
            )}
        </div>
    );
};

export default TransferRecieptOthersVerification;
